import { PALETTE } from "../core/constants";
import { Container, Graphics, Sprite, Texture } from "pixi.js";
import type { JellyVisualDefinition } from "../../generated/content-types";

export interface JellyState {
  fill: number;
  pending: boolean;
  radius: number;
  restRadius?: number;
  seed?: number;
  scaleX: number;
  scaleY: number;
}
export const expressionNames = [
  "悠闲",
  "满足",
  "吃撑",
  "憋住",
  "极限",
  "待爆",
] as const;
export function expressionPresets(style: JellyVisualDefinition["style"]) {
  return style === "classic"
    ? [
        { name: "平静", fill: 0.2, pending: false },
        { name: "鼓起", fill: 0.7, pending: false },
        { name: "待爆", fill: 1, pending: true },
      ]
    : expressionNames.map((name, index) => ({
        name,
        fill: [0.12, 0.37, 0.62, 0.82, 0.96, 1][index],
        pending: index === 5,
      }));
}
const thresholds = [0.25, 0.5, 0.75, 0.9];
const poses = [
  [1, 0, 0, 0, 13, 8, 1],
  [0, 1, 0, 0, 15, 12, 1],
  [1.25, 0, 0, 0, 7, 9, 0],
  [0.35, 0, 0, 0, 7, 14, 0],
  [0, 0, 1, 0, 6, 6, 0],
  [0, 0, 0, 1, 13, 18, 0],
];

/** Shared by the game and workbench. No gameplay writes, filesystem access or global clock. */
export class JellyView {
  readonly container = new Container();
  private body = new Sprite();
  private face = new Graphics();
  private badge = new Graphics();
  private time = 0;
  private hitAt = -100;
  private stage = 1;
  private pose = [...poses[1]];
  private from = [...poses[1]];
  private target = [...poses[1]];
  private faceKey = "";
  private changedAt = -100;
  private key = "";
  private state: JellyState = {
    fill: 0.35,
    pending: false,
    radius: 18,
    scaleX: 1,
    scaleY: 1,
  };
  constructor(
    private params: JellyVisualDefinition,
    texture: Texture,
  ) {
    this.body.texture = texture;
    this.body.anchor.set(0.5);
    this.container.addChild(this.body, this.face, this.badge);
    this.badge
      .circle(0, 0, 24)
      .stroke({ color: PALETTE.cream, width: 3, alpha: 0.75 });
    this.step(0);
  }
  configure(params: JellyVisualDefinition, texture: Texture) {
    if (this.params.style !== params.style) this.reset();
    this.params = params;
    this.body.texture = texture;
    this.faceKey = "";
  }
  /** Mutators stage inputs; step(0) explicitly refreshes a paused view. */
  setState(state: JellyState) {
    Object.assign(this.state, state);
    this.state.fill = Math.max(0, Math.min(1, state.fill));
    this.state.restRadius = state.restRadius ?? 13;
    this.state.seed = state.seed ?? 0;
  }
  hit() {
    this.hitAt = this.time;
  }
  reset() {
    this.time = 0;
    this.hitAt = -100;
    this.stage = 1;
    this.pose = [...poses[1]];
    this.from = [...this.pose];
    this.target = [...this.pose];
    this.changedAt = -100;
    this.key = "";
    this.faceKey = "";
  }
  step(dt: number) {
    if (!Number.isFinite(dt) || dt < 0 || dt > 1)
      throw new Error("步长需在 0–1 秒");
    if (this.params.style === "soft") {
      while (this.stage < 4 && this.state.fill >= thresholds[this.stage])
        this.stage++;
      while (
        this.stage > 0 &&
        this.state.fill < thresholds[this.stage - 1] - 0.02
      )
        this.stage--;
      // Process new inputs at the current time, before advancing the clock.
      this.updatePose();
      let left = dt;
      while (left > 0) {
        const increment = Math.min(left, 1 / 60);
        this.time += increment;
        left -= increment;
        this.updatePose();
      }
    } else {
      this.time += dt;
    }
    this.draw();
  }
  snapshot() {
    return {
      time: this.time,
      fill: this.state.fill,
      pending: this.state.pending,
      expression:
        this.params.style === "classic"
          ? ["平静", "鼓起", "待爆"][
              this.state.pending ? 2 : this.state.fill >= 0.55 ? 1 : 0
            ]
          : expressionNames[this.state.pending ? 5 : this.stage],
      state: this.state.pending
        ? "pending"
        : this.time - this.hitAt < 0.28
          ? "hit"
          : "idle",
    };
  }
  private sample() {
    const t = Math.min(
      1,
      Math.max(0, (this.time - this.changedAt) / this.params.transition),
    );
    const ease = t * t * (3 - 2 * t);
    for (let i = 0; i < this.pose.length; i++)
      this.pose[i] = this.from[i] + (this.target[i] - this.from[i]) * ease;
  }
  private updatePose() {
    this.sample();
    const hit = this.time - this.hitAt < 0.28 && !this.state.pending;
    const key = `${this.state.pending ? 5 : this.stage}:${hit}`;
    if (key === this.key) return;
    this.key = key;
    this.from = [...this.pose];
    this.target = [...poses[this.state.pending ? 5 : this.stage]];
    if (hit)
      this.target =
        this.stage === 2 ? [1.5, 0, 0, 0, 11, 15, 0] : [0, 0, 1, 0, 7, 9, 0];
    this.changedAt = this.time;
  }
  private draw() {
    if (this.params.style === "classic") this.drawClassic();
    else this.drawSoft();
  }
  private hitDeformation(): number {
    const age = this.time - this.hitAt,
      p = this.params;
    return age < p.recovery
      ? p.softness *
          Math.cos((age / p.recovery) * Math.PI * 3) *
          (1 - age / p.recovery) ** 2
      : 0;
  }
  private bodySize(): number {
    const p = this.params,
      t = this.time;
    const pulse = this.state.pending
      ? Math.max(0, Math.sin(t * 9)) ** 4 * p.pulse
      : 0;
    return (
      (this.state.radius / 96) * (1 + Math.sin(t * 2.7) * p.breathe + pulse)
    );
  }
  private drawSoft() {
    const p = this.params,
      s = this.state,
      t = this.time,
      face = this.face;
    const hit = this.hitDeformation();
    const size = this.bodySize();
    const intensity = s.pending
      ? 1
      : this.stage === 4
        ? 0.65
        : this.stage === 3
          ? 0.3
          : 0;
    const cycle = t % (s.pending ? 0.48 : this.stage === 4 ? 0.8 : 1.3);
    const shake =
      cycle < 0.24
        ? Math.sin(t * p.shakeFrequency * Math.PI * 2) *
          p.shakeAmplitude *
          intensity *
          Math.sin((cycle / 0.24) * Math.PI)
        : 0;
    this.body.alpha = p.opacity;
    this.body.position.set(shake * size, 0);
    this.body.scale.set(
      ((size * 224) / 448) * s.scaleX * (1 + hit),
      (((size * 224) / 448) * s.scaleY) / (1 + hit),
    );
    face.position.set(shake * size, p.faceY * size);
    face.scale.set(
      size * (1 + (s.scaleX - 1) * p.faceFollow) * (1 + hit * p.faceFollow),
      (size * (1 + (s.scaleY - 1) * p.faceFollow)) / (1 + hit * p.faceFollow),
    );
    this.body.rotation = face.rotation =
      !s.pending && this.stage < 2 ? Math.sin(t * 1.8) * p.wobble : 0;
    this.badge.visible = false;
    const blink =
      !s.pending && this.stage < 3
        ? Math.max(0, 1 - Math.abs((t % 3.7) - 3.4) / 0.075)
        : 0;
    let faceKey = blink.toFixed(2);
    for (const value of this.pose) faceKey += ":" + value.toFixed(2);
    if (faceKey !== this.faceKey) {
      this.faceKey = faceKey;
      face.clear();
      const ink = 0x173e38;
      for (const sign of [-1, 1]) {
        const x = sign * p.eyeSpacing,
          open = this.pose[0] * (1 - blink);
        if (open > 0.001) {
          face
            .ellipse(x, -5, 8, 11 * open)
            .fill({ color: ink, alpha: Math.min(1, open) });
          face
            .ellipse(x - 2.2, -5 - 3.6 * open, 2.2, 2.8 * open)
            .fill({ color: 0xf4ffe7, alpha: 0.9 * Math.min(1, open) });
        }
        if (this.pose[1] > 0.001)
          face
            .moveTo(x - 9, -5)
            .quadraticCurveTo(x, -17, x + 9, -5)
            .stroke({
              color: ink,
              width: 4,
              alpha: this.pose[1],
              cap: "round",
            });
        if (this.pose[2] > 0.001)
          face
            .moveTo(x + sign * 8, -14)
            .lineTo(x - sign * 4, -5)
            .lineTo(x + sign * 8, 4)
            .stroke({
              color: ink,
              width: 4,
              alpha: this.pose[2],
              cap: "round",
              join: "round",
            });
        if (this.pose[3] > 0.001)
          face
            .moveTo(x - 8, -13)
            .lineTo(x + 8, 3)
            .moveTo(x - 8, 3)
            .lineTo(x + 8, -13)
            .stroke({
              color: ink,
              width: 4,
              alpha: this.pose[3],
              cap: "round",
            });
        if (blink > 0 && this.pose[0] > 0)
          face
            .moveTo(x - 8, -5)
            .lineTo(x + 8, -5)
            .stroke({
              color: ink,
              width: 3,
              alpha: blink * Math.min(1, this.pose[0]),
            });
        // Soft cheeks are drawn as nested translucent ellipses, no per-instance blur filter.
        for (let i = 4; i > 0; i--)
          face
            .ellipse(sign * 43, 17, 6 + i * 2.5, 3 + i * 1.5)
            .fill({ color: 0xefa373, alpha: 0.035 });
      }
      const smile = this.pose[6],
        w = this.pose[4] * (p.mouthSize + (1 - p.mouthSize) * smile),
        h = this.pose[5] * (p.mouthSize + (1 - p.mouthSize) * smile);
      face
        .moveTo(-w, 27)
        .bezierCurveTo(
          -w,
          27 - h + smile * h * 1.15,
          w,
          27 - h + smile * h * 1.15,
          w,
          27,
        )
        .bezierCurveTo(w, 27 + h, -w, 27 + h, -w, 27)
        .closePath()
        .fill(ink)
        .stroke({ color: ink, width: 2 });
      if (smile > 0.01)
        face
          .ellipse(0, 27 + h * 0.45, 5, 2.4)
          .fill({ color: 0xf1a8a7, alpha: smile });
    }
  }
  private drawClassic() {
    const p = this.params,
      s = this.state,
      t = this.time,
      face = this.face;
    const size = this.bodySize(),
      hit = this.hitDeformation();
    this.body.alpha = 1;
    this.body.position.set(0, 0);
    this.body.scale.set(
      size * 0.5 * s.scaleX * (1 + hit),
      (size * 0.5 * s.scaleY) / (1 + hit),
    );

    const ratio = s.radius / (s.restRadius ?? 13),
      tier = s.pending ? 2 : s.fill >= 0.55 ? 1 : 0;
    const rotation = Math.sin(t * 3.2 + (s.seed ?? 0) * 8) * p.wobble;
    this.body.rotation = face.rotation = rotation;
    face.position.set(0, p.faceY * size);
    face.scale.set(ratio * s.scaleX, ratio * s.scaleY);
    const key = `classic:${tier}:${p.eyeSpacing}:${p.mouthSize}`;
    if (this.faceKey !== key) {
      this.faceKey = key;
      drawClassicFace(face, tier, (p.eyeSpacing / 29) * 6, p.mouthSize);
    }
    this.badge.visible = s.pending;
    this.badge.rotation = rotation;
    const beat = 1 + Math.sin(t * 22) * 0.08;
    this.badge.scale.set(ratio * s.scaleX * beat, ratio * s.scaleY * beat);
  }
  destroy() {
    this.container.destroy({ children: true });
  }
}

function drawClassicFace(
  face: Graphics,
  tier: number,
  eyeSpacing = 6,
  mouthSize = 1,
): void {
  face.clear();
  if (tier === 0) {
    face
      .circle(-eyeSpacing, 1, 2.3)
      .fill(PALETTE.ink)
      .circle(eyeSpacing, 1, 2.3)
      .fill(PALETTE.ink);
    face
      // Preserve the original diagonal lead-in to the smile arc.
      .moveTo(0, 0)
      .arc(0, 5, 5 * mouthSize, 0.2, Math.PI - 0.2)
      .stroke({ color: PALETTE.ink, width: 1.8 });
  } else if (tier === 1) {
    face
      .circle(-8, 3, 3.6)
      .fill({ color: 0xff7a9a, alpha: 0.35 })
      .circle(8, 3, 3.6)
      .fill({ color: 0xff7a9a, alpha: 0.35 });
    face
      .circle(-eyeSpacing, 0, 2.8)
      .fill(PALETTE.ink)
      .circle(eyeSpacing, 0, 2.8)
      .fill(PALETTE.ink);
    face
      .circle(0, 6, 2.4 * mouthSize)
      .stroke({ color: PALETTE.ink, width: 1.8 });
  } else {
    face
      .circle(-8, 4, 4.2)
      .fill({ color: 0xff5f86, alpha: 0.5 })
      .circle(8, 4, 4.2)
      .fill({ color: 0xff5f86, alpha: 0.5 });
    face
      .moveTo(-9, -3)
      .lineTo(-3, 1)
      .moveTo(-9, 1)
      .lineTo(-3, -3)
      .moveTo(3, 1)
      .lineTo(9, -3)
      .moveTo(3, -3)
      .lineTo(9, 1)
      .stroke({ color: PALETTE.ink, width: 2 });
    face
      .moveTo(-6 * mouthSize, 8)
      .lineTo(6 * mouthSize, 8)
      .stroke({ color: PALETTE.ink, width: 2.2 });
  }
}
